import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const SECTION_LINKS = [
  { label: "The X", href: "#the-x" },
  { label: "Protocol", href: "#how-it-works" },
  { label: "Network", href: "#who" },
  { label: "FINDX AI", href: "#findx-ai" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const prefix = pathname === "/" ? "" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-[#05070B]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href={`${prefix}#top`} data-testid="brand-logo" className="flex items-center gap-3 group">
          <span className="w-9 h-9 grid place-items-center border border-[#00F0FF]/50 font-display font-extrabold text-lg text-[#00F0FF] transition-shadow duration-300 group-hover:shadow-[0_0_20px_rgba(0,240,255,0.5)]">
            X
          </span>
          <span className="font-display font-bold tracking-[0.2em] text-sm text-slate-100">
            FINDXCHANGE
          </span>
        </a>

        <nav className="hidden xl:flex items-center gap-8" data-testid="nav-links">
          {SECTION_LINKS.map((l) => (
            <a
              key={l.href}
              href={`${prefix}${l.href}`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="font-mono-accent text-[11px] uppercase tracking-[0.28em] text-slate-400 hover:text-[#00F0FF] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <a
            href="/opportunities"
            data-testid="nav-link-opportunities"
            className={`font-mono-accent text-[11px] uppercase tracking-[0.28em] transition-colors duration-300 ${
              pathname === "/opportunities" ? "text-[#00F0FF]" : "text-slate-400 hover:text-[#00F0FF]"
            }`}
          >
            Opportunities
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden md:block">
            <a href="/post" data-testid="nav-post-btn" className="btn-ghost !px-6 !py-3">
              Post to the X
            </a>
          </span>
          <span className="hidden sm:block">
            <a href={`${prefix}#founding-100`} data-testid="nav-become-founder-btn" className="btn-primary !px-6 !py-3">
              Become a Founder
            </a>
          </span>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden w-10 h-10 grid place-items-center border border-white/15 text-slate-300"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="xl:hidden overflow-hidden border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {SECTION_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={`${prefix}${l.href}`}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="font-mono-accent text-xs uppercase tracking-[0.28em] text-slate-300 hover:text-[#00F0FF] transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/opportunities"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-opportunities"
                className="font-mono-accent text-xs uppercase tracking-[0.28em] text-[#00F0FF]"
              >
                Opportunities
              </a>
              <a
                href="/post"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-post"
                className="font-mono-accent text-xs uppercase tracking-[0.28em] text-slate-300 hover:text-[#00F0FF] transition-colors"
              >
                Post to the X
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
